import { clamp, clamp01, inverseLerp, lerp, snapToStep } from '../../math/scalar.js';
import { RAD2DEG } from '../../math/angle.js';
import type { ValueSubtype, ValueUnit } from '../../graph/nodeDefs.js';

export interface DragFieldHistory {
  onBegin: () => void;
  onEnd: (changed: boolean) => void;
}

export interface DragFieldVisualOptions {
  showProgress?: boolean;
  decimals?: number;
  suffix?: string;
  className?: string;
  subtype?: ValueSubtype;
  unit?: ValueUnit;
}

/** Canonical storage unit for a subtype, used when `unit` is omitted. */
function defaultUnit(subtype: ValueSubtype | undefined): ValueUnit | undefined {
  if (subtype === 'angle') return 'radians';
  if (subtype === 'percentage') return 'fraction';
  if (subtype === 'em') return 'em';
  if (subtype === 'pixels' || subtype === 'distance') return 'px';
  return undefined;
}

/**
 * Factor to multiply a stored value by to get its display value (and divide by
 * to go back). Keeps storage and presentation as separate axes: e.g. angles are
 * stored in radians but shown in degrees, percentages stored as 0..1 but shown
 * as 0..100. Returns 1 when the stored unit already matches the display unit.
 */
function displayScale(subtype: ValueSubtype | undefined, unit: ValueUnit | undefined): number {
  switch (unit ?? defaultUnit(subtype)) {
    case 'radians': return RAD2DEG;
    case 'fraction': return 100;
    default: return 1; // degrees, percent, px, undefined — shown as stored
  }
}

export interface DragFieldElement extends HTMLElement {
  setValue: (value: number) => void;
}

export function createDragger(
  initial: number,
  min: number,
  max: number,
  step: number,
  onChange: (v: number) => void,
  history?: DragFieldHistory,
  visual?: DragFieldVisualOptions,
): DragFieldElement {
  const subtype = visual?.subtype;

  // ── Storage ⇄ display unit ───────────────────────────────────────────
  // The dragger is the single place that converts a value's storage unit to its
  // display unit (e.g. radians→degrees, 0..1→0..100). It works internally in the
  // display unit and converts at the I/O boundary (initial, min/max/step,
  // onChange, setValue). Scale of 1 means stored and shown units already match.
  const scale = displayScale(subtype, visual?.unit);
  if (scale !== 1) {
    initial *= scale;
    min *= scale;
    max *= scale;
    step *= scale;
  }
  const emitChange = scale !== 1 ? (v: number): void => onChange(v / scale) : onChange;

  // ── Subtype-based defaults ──────────────────────────────────────────
  let decimals = visual?.decimals ?? (step >= 1 ? 0 : step >= 0.1 ? 1 : 2);
  let suffix = visual?.suffix ?? '';
  let showProgress = Boolean(visual?.showProgress && Number.isFinite(min) && Number.isFinite(max) && max > min);

  if (subtype) {
    switch (subtype) {
      case 'angle':
        suffix = visual?.suffix ?? '°';
        decimals = visual?.decimals ?? 1;
        // No wrap — angles are cyclic; let the value grow/fall freely.
        break;
      case 'factor':
        suffix = visual?.suffix ?? '';
        decimals = visual?.decimals ?? 3;
        showProgress = visual?.showProgress ?? (min === 0 && max === 1);
        break;
      case 'percentage':
        suffix = visual?.suffix ?? '%';
        decimals = visual?.decimals ?? 1;
        showProgress = visual?.showProgress ?? true;
        break;
      case 'pixels':
        suffix = visual?.suffix ?? 'px';
        decimals = visual?.decimals ?? 0;
        break;
      case 'distance':
        suffix = visual?.suffix ?? 'px';
        decimals = visual?.decimals ?? 1;
        break;
      case 'em':
        // Font-relative length (1 = one em); scales with the font size.
        suffix = visual?.suffix ?? 'em';
        decimals = visual?.decimals ?? 3;
        break;
      case 'color':
        decimals = visual?.decimals ?? 3;
        suffix = visual?.suffix ?? '';
        break;
      default:
        // 'default' — no overrides
        break;
    }
  }

  // ── Override with explicit visual options ───────────────────────────
  if (visual?.suffix !== undefined) suffix = visual.suffix;
  if (visual?.decimals !== undefined) decimals = visual.decimals;
  if (visual?.showProgress !== undefined) showProgress = visual.showProgress;

  function formatDisplay(value: number): string {
    return value.toFixed(decimals).replace(/(\.\d*?[1-9])0+$|\.0+$/, '$1');
  }

  function snap(v: number): number {
    const snapped = snapToStep(v, step);
    if (subtype === 'angle') {
      // Cyclic — never clamp. Consumer wraps via modulo on render.
      return parseFloat(snapped.toFixed(decimals));
    }
    return parseFloat(clamp(snapped, min, max).toFixed(decimals));
  }

  let current = snap(initial);
  let progressValue = current;
  const isCyclicAngle = subtype === 'angle';

  const wrap = Object.assign(document.createElement('div'), {
    setValue: (_value: number): void => {},
  });
  wrap.className = 'drag-field';
  if (visual?.className) wrap.classList.add(visual.className);
  if (showProgress) {
    wrap.classList.add('drag-field--with-progress');
  }

  const label = document.createElement('span');
  label.className = 'drag-field-text';

  wrap.appendChild(label);

  // Narrow no-break space keeps the unit from crowding the number (and from
  // wrapping onto a new line) — applied once for every suffix, not per subtype.
  const suffixSep = suffix ? ' ' : '';
  function progressRatio(value: number): number {
    if (!isCyclicAngle) return clamp01(inverseLerp(min, max, value));
    const span = max - min;
    if (!Number.isFinite(span) || span <= 0) return 0;
    return ((((value - min) % span) + span) % span) / span;
  }

  function refresh(): void {
    label.textContent = `${formatDisplay(current)}${suffixSep}${suffix}`;
    if (showProgress) {
      wrap.style.setProperty('--drag-progress', `${progressRatio(progressValue) * 100}%`);
    }
  }
  refresh();

  wrap.setValue = (value: number): void => {
    current = snap(scale !== 1 ? value * scale : value);
    progressValue = current;
    refresh();
  };

  let startX = 0;
  let startVal = 0;
  let startProgress = 0;
  let startPointerRatio = 0;
  let hasMoved = false;
  let historyStarted = false;
  let changedDuringDrag = false;
  let activeEditor: HTMLInputElement | null = null;

  function pointerRatio(clientX: number): number {
    const rect = wrap.getBoundingClientRect();
    if (rect.width <= 0) return 0;
    return (clientX - rect.left) / rect.width;
  }

  function progressFromDrag(clientX: number): number {
    const progressRatio = clamp01(
      inverseLerp(min, max, startProgress) + (pointerRatio(clientX) - startPointerRatio),
    );
    return lerp(min, max, progressRatio);
  }

  function cyclicProgressFromDrag(clientX: number): number {
    return startProgress + (pointerRatio(clientX) - startPointerRatio) * (max - min);
  }

  function cleanupDrag(allowInlineEdit: boolean): void {
    window.removeEventListener('mousemove', onMove);
    window.removeEventListener('mouseup', onUp);
    window.removeEventListener('blur', onCancel);
    wrap.classList.remove('drag-field--dragging');
    if (showProgress) {
      progressValue = current;
      refresh();
    }
    if (history && historyStarted) history.onEnd(changedDuringDrag);
    historyStarted = false;
    changedDuringDrag = false;
    if (allowInlineEdit && !hasMoved) enterEdit();
  }

  function onMove(e: MouseEvent): void {
    const dx = e.clientX - startX;
    if (!hasMoved && Math.abs(dx) > 3) hasMoved = true;
    if (!hasMoved) return;
    const progressDragValue = showProgress
      ? isCyclicAngle
        ? cyclicProgressFromDrag(e.clientX)
        : progressFromDrag(e.clientX)
      : null;
    const next = snap(progressDragValue ?? (startVal + dx * step * (e.shiftKey ? 0.1 : 1)));
    if (showProgress) {
      progressValue = progressDragValue ?? next;
    }
    if (next === current) {
      if (showProgress) refresh();
      return;
    }
    if (history && !historyStarted) {
      history.onBegin();
      historyStarted = true;
    }
    changedDuringDrag = true;
    current = next;
    refresh();
    emitChange(current);
  }

  function onUp(): void {
    cleanupDrag(true);
  }

  function onCancel(): void {
    cleanupDrag(false);
  }

  wrap.addEventListener('mousedown', (e: MouseEvent) => {
    if (e.button !== 0) return;
    if (wrap.classList.contains('drag-field--disabled')) return;
    if (activeEditor) return;
    e.preventDefault();
    e.stopPropagation();
    wrap.classList.add('drag-field--dragging');
    startX = e.clientX;
    startVal = current;
    startProgress = progressValue;
    startPointerRatio = pointerRatio(e.clientX);
    hasMoved = false;
    historyStarted = false;
    changedDuringDrag = false;
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('blur', onCancel);
  });

  function enterEdit(): void {
    if (activeEditor) return;
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'decimal';
    input.className = 'drag-field-edit';
    input.value = formatDisplay(current);
    activeEditor = input;
    label.style.visibility = 'hidden';
    wrap.appendChild(input);
    input.focus();
    input.select();
    let done = false;
    function commit(): void {
      if (done) return;
      done = true;
      const v = parseFloat(input.value.replace(',', '.'));
      if (isFinite(v)) {
        const next = snap(v);
        progressValue = next;
        if (next !== current) {
          if (history) history.onBegin();
          current = next;
          refresh();
          emitChange(current);
          if (history) history.onEnd(true);
        } else {
          refresh();
        }
      }
      activeEditor = null;
      input.remove();
      label.style.visibility = '';
    }
    function cancel(): void {
      if (done) return;
      done = true;
      activeEditor = null;
      input.remove();
      label.style.visibility = '';
    }
    input.addEventListener('mousedown', e => e.stopPropagation());
    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (ke: KeyboardEvent) => {
      if (ke.key === 'Enter') { ke.preventDefault(); commit(); }
      else if (ke.key === 'Escape') { ke.preventDefault(); cancel(); }
    });
  }

  return wrap;
}
